import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';

const Register = () => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validate username
    if (!username || username.trim().length === 0) {
      setError('Username is required');
      return;
    }

    if (username.length < 3 || username.length > 30) {
      setError('Username must be 3-30 characters long');
      return;
    }

    const usernameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!usernameRegex.test(username)) {
      setError('Username must contain only letters, numbers, hyphens, and underscores');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      await register(email, username, password);
      setSuccessMessage('Registration successful! Redirecting to login page...');
      
      // Redirect to login page after successful registration
      setTimeout(() => {
        navigate('/login');
      }, 2000);
      
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" rel="stylesheet" />
      
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.logo}>
            <i className="fas fa-graduation-cap" style={styles.logoIcon}></i>
            <span style={styles.logoText}>Smart Learning Portal</span>
          </div>
        </div>
      </header>

      <div style={styles.registerSection}>
        <div style={styles.registerContainer}>
          <div style={styles.registerCard}>
            <div style={styles.registerHeader}>
              <h1 style={styles.registerTitle}>Create Account</h1>
              <p style={styles.registerSubtitle}>Sign up to start your learning journey</p>
            </div>

            {error && (
              <div style={styles.errorAlert}>
                <i className="fas fa-exclamation-circle" style={styles.errorIcon}></i>
                <span style={styles.errorText}>{error}</span>
              </div>
            )}

            {successMessage && (
              <div style={styles.successAlert}>
                <i className="fas fa-check-circle" style={styles.successIcon}></i>
                <span style={styles.successText}>{successMessage}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} style={styles.registerForm}>
              <div style={styles.inputGroup}>
                <label htmlFor="email" style={styles.inputLabel}>
                  <i className="fas fa-envelope" style={styles.inputIcon}></i>
                  Email Address
                </label>
                <input
                  type="email"
                  style={styles.inputField}
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label htmlFor="username" style={styles.inputLabel}>
                  <i className="fas fa-user" style={styles.inputIcon}></i>
                  Username
                </label>
                <input
                  type="text"
                  style={styles.inputField}
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter a username"
                  required
                  minLength="3"
                  maxLength="30"
                  pattern="[a-zA-Z0-9_-]+"
                />
                <div style={styles.passwordHint}>
                  3-30 characters: letters, numbers, hyphens, underscores only
                </div>
              </div>

              <div style={styles.inputGroup}>
                <label htmlFor="password" style={styles.inputLabel}>
                  <i className="fas fa-lock" style={styles.inputIcon}></i>
                  Password
                </label>
                <div style={styles.passwordWrapper}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    style={{...styles.inputField, ...styles.inputWithToggle}}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password"
                    required
                    minLength="8"
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword(s => !s)}
                    style={styles.eyeButton}
                  >
                    <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                  </button>
                </div>
                <div style={styles.passwordHint}>
                  Must be at least 8 characters long
                </div>
              </div>

              <div style={styles.inputGroup}>
                <label htmlFor="confirmPassword" style={styles.inputLabel}>
                  <i className="fas fa-lock" style={styles.inputIcon}></i>
                  Confirm Password
                </label>
                <div style={styles.passwordWrapper}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    style={{...styles.inputField, ...styles.inputWithToggle}}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                  />
                  <button
                    type="button"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowConfirmPassword(s => !s)}
                    style={styles.eyeButton}
                  >
                    <i className={showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                  </button>
                </div>
              </div>

              <button
                type="submit"
                style={{
                  ...styles.registerButton,
                  ...(loading ? styles.registerButtonLoading : {})
                }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div style={styles.spinner}></div>
                    Creating Account...
                  </>
                ) : (
                  <>
                    <i className="fas fa-user-plus" style={styles.buttonIcon}></i>
                    Create Account
                  </>
                )}
              </button>
            </form>

            <div style={styles.loginSection}>
              <p style={styles.loginText}>
                Already have an account?{' '}
                <Link to="/login" style={styles.loginLink}>Sign in</Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <p style={styles.footerText}>
            © 2025 Cognify Analytics. All rights reserved.
          </p>
          <div style={styles.footerLinks}>
            <a href="#" style={styles.footerLink}>Privacy Policy</a>
            <a href="#" style={styles.footerLink}>Terms of Service</a>
            <a href="#" style={styles.footerLink}>Contact Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    fontFamily: '"Inter", sans-serif',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e5e7eb',
    padding: '16px 0',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logoIcon: {
    fontSize: '32px',
    color: '#4f46e5',
  },
  logoText: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  registerSection: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  registerContainer: {
    width: '100%',
    maxWidth: '440px',
  },
  registerCard: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '40px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    border: '1px solid #e5e7eb',
  },
  registerHeader: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  registerTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#111827',
    margin: '0 0 8px 0',
  },
  registerSubtitle: {
    fontSize: '16px',
    color: '#6b7280',
    margin: 0,
  },
  errorAlert: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  errorIcon: {
    color: '#dc2626',
    fontSize: '16px',
  },
  errorText: {
    color: '#dc2626',
    fontSize: '14px',
    fontWeight: '500',
  },
  successAlert: {
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  successIcon: {
    color: '#16a34a',
    fontSize: '16px',
  },
  successText: {
    color: '#16a34a',
    fontSize: '14px',
    fontWeight: '500',
  },
  registerForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  inputLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  inputIcon: {
    color: '#6b7280',
    fontSize: '14px',
    width: '16px',
  },
  inputField: {
    padding: '12px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '16px',
    color: '#111827',
    backgroundColor: '#ffffff',
    transition: 'all 0.2s ease',
    fontFamily: '"Inter", sans-serif',
  },
  passwordWrapper: {
    position: 'relative',
    width: '100%',
  },
  inputWithToggle: {
    width: '100%',
    paddingRight: '40px',
    boxSizing: 'border-box',
  },
  eyeButton: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#6b7280',
    fontSize: '16px',
    padding: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  passwordHint: {
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '4px',
  },
  registerButton: {
    backgroundColor: '#4f46e5',
    border: 'none',
    borderRadius: '8px',
    padding: '14px 20px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontFamily: '"Inter", sans-serif',
  },
  registerButtonLoading: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid transparent',
    borderTop: '2px solid #ffffff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  buttonIcon: {
    fontSize: '16px',
  },
  loginSection: {
    marginTop: '24px',
    textAlign: 'center',
    paddingTop: '24px',
    borderTop: '1px solid #e5e7eb',
  },
  loginText: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0,
  },
  loginLink: {
    color: '#4f46e5',
    textDecoration: 'none',
    fontWeight: '600',
    transition: 'color 0.2s ease',
  },
  footer: {
    backgroundColor: '#111827',
    color: '#ffffff',
    padding: '24px 0',
  },
  footerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '16px',
  },
  footerText: {
    fontSize: '14px',
    color: '#9ca3af',
    margin: 0,
  },
  footerLinks: {
    display: 'flex',
    gap: '24px',
  },
  footerLink: {
    fontSize: '14px',
    color: '#9ca3af',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
  },
};

const additionalStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  input:focus {
    outline: none;
    border-color: #4f46e5;
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
  }

  button:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .login-link:hover {
    color: #3730a3;
  }

  .footer-link:hover {
    color: #ffffff;
  }

  @media (max-width: 640px) {
    .register-card {
      padding: 24px;
      margin: 16px;
    }
    
    .footer-content {
      flex-direction: column;
      text-align: center;
    }
    
    .footer-links {
      justify-content: center;
    }
  }
`;

const styleSheet = document.createElement('style');
styleSheet.innerText = additionalStyles;
document.head.appendChild(styleSheet);

export default Register;